import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import LoaderComponent from '../components/loader.component';

const Login = lazy(() => import('../containers/login.container'));
const Home = lazy(() => import('../containers/home.container'));
const ResetPassword = lazy(() => import('../containers/reset-password.container'));
const ChangePassword = lazy(() => import('../containers/change-password.container'));
const UserManagement = lazy(() => import('../containers/users.container'));
const PartnerManagement = lazy(() => import('../containers/partners.container'));
const Customers = lazy(() => import('../containers/customers.container'));
const Sites = lazy(() => import('../containers/sites.container'));
const Locations = lazy(() => import('../containers/locations.container'));
const Floors = lazy(() => import('../containers/floors.container'));
const Pantries = lazy(() => import('../containers/pantries.container'));
const Machines = lazy(() => import('../containers/machines.container'));
const Inventory = lazy(() => import('../containers/inventory-supply.container'));
const LegacyInventory = lazy(() => import('../containers/inventory.container'));
const Supply = lazy(() => import('../containers/reconciliation.container'));
const PantryConsole = lazy(() => import('../containers/pantry-console.container'));
const Maintenance = lazy(() => import('../containers/maintenance.container'));
const Engineer = lazy(() => import('../containers/engineer.container'));
const Pantry = lazy(() => import('../containers/pantry.container'));
const PantryStaff = lazy(() => import('../containers/pantry-staff.container'));
const Complaints = lazy(() => import('../containers/complaints.container'));
const Engineers = lazy(() => import('../containers/engineers.container'));
const RecipeCalibration = lazy(() => import('../containers/recipe-calibration.container'));

const propTypes = {
    isLoggedIn: PropTypes.bool,
    userInfo: PropTypes.object
};

const defaultProps = {
    isLoggedIn: false,
    userInfo: {}
};

const PreventBackRoute = ({ isLoggedIn, children }) => {
    if (isLoggedIn) {
        return <Navigate to='/home' replace={true} />;
    }
    return children;
};

/**
 * Pages a field engineer is allowed to open. Everything else in this app is a
 * manager tool — the engineer's whole world is /engineer. The API enforces this
 * too, but a technician landing on a supply dashboard is confusing at best and
 * an information leak at worst, so the router turns them around here.
 */
const ENGINEER_ALLOWED = ['/engineer', '/reset-password', '/change-password'];
/**
 * Pantry staff live in the pantry app, plus Inventory & Supply: they are the
 * people physically holding the stock, so they record opening counts, receipts
 * and corrections for the pantries their machines sit in. The backend scopes
 * them to exactly those pantries — this list only decides which URLs the router
 * will let them open.
 */
const PANTRY_ALLOWED = ['/pantry', '/inventory', '/reset-password', '/change-password'];

const ProtectedRoute = ({ isLoggedIn, userInfo, children }) => {
    const location = useLocation();

    if (!isLoggedIn) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (userInfo?.status !== 1 && location.pathname !== '/reset-password') {
        return <Navigate to="/reset-password" state={{ from: location }} replace />;
    }

    // Send engineers to their own app, wherever they were headed.
    if (userInfo?.role === 'MACHINE_ADMIN' && !ENGINEER_ALLOWED.includes(location.pathname)) {
        return <Navigate to="/engineer" replace />;
    }

    // Pantry staff (FLOOR_ADMIN) live entirely in the pantry app.
    if (userInfo?.role === 'FLOOR_ADMIN' && !PANTRY_ALLOWED.includes(location.pathname)) {
        return <Navigate to="/pantry" replace />;
    }

    return children;
};

const AppRoutes = ({ isLoggedIn, userInfo }) => {

    return (
        <Suspense fallback={<LoaderComponent />}>
            <Routes>
                <Route exact path='/login' element={<PreventBackRoute isLoggedIn={isLoggedIn}> <Login /></PreventBackRoute>} />
                <Route exact path='/' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo} ><Home /></ProtectedRoute>} replace={true} />
                <Route exact path='/home' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><Home /></ProtectedRoute>} replace={true} />
                    <Route exact path='/pantry-console' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><PantryConsole /></ProtectedRoute>} replace={true} />
                <Route exact path='/reset-password' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><ResetPassword /></ProtectedRoute>} replace={true} />
                <Route exact path='/change-password' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><ChangePassword /></ProtectedRoute>} replace={true} />
                <Route exact path='/user-management' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><UserManagement /></ProtectedRoute>} replace={true} />
                <Route exact path='/partners' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><PartnerManagement /></ProtectedRoute>} replace={true} />
                <Route exact path='/customers' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><Customers /></ProtectedRoute>} replace={true} />
                <Route exact path='/sites' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><Sites /></ProtectedRoute>} replace={true} />
                {/*
                  The estate workspace keeps its selection in the URL so the back
                  button works and a pantry can be linked to. `estate` is a literal
                  second segment, which keeps these clear of the older
                  /sites/:siteId/locations drill-down routes below.
                */}
                <Route exact path='/sites/estate/:customerId' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><Sites /></ProtectedRoute>} replace={true} />
                <Route exact path='/sites/estate/:customerId/:nodeType/:nodeId' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><Sites /></ProtectedRoute>} replace={true} />
                <Route exact path='/sites/:siteId/locations' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><Locations /></ProtectedRoute>} replace={true} />
                <Route exact path='/sites/:siteId/locations/:locationId/floors' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><Floors /></ProtectedRoute>} replace={true} />
                <Route exact path='/sites/:siteId/locations/:locationId/floors/:floorId/pantries' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><Pantries /></ProtectedRoute>} replace={true} />
                <Route exact path='/machines' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><Machines /></ProtectedRoute>} replace={true} />
                {/*
                  Recipe calibration sits beside Machine management because it
                  configures what a machine pours. It is an admin tool: the
                  engineer and pantry-staff allow-lists above do not name it, so
                  those roles are turned around before it renders - and the API
                  refuses the writes independently of this router.
                */}
                <Route exact path='/recipe-calibration' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><RecipeCalibration /></ProtectedRoute>} replace={true} />
                {/*
                  /inventory is the Inventory & Supply workspace (Overview, Stock,
                  Consumption, Forecast, Procurement, Movements) built on the
                  trusted-Brew layer.

                  /supply is UNCHANGED and stays routable: it remains the home of
                  delivery CRUD, the supplied-vs-consumed reconciliation and the
                  supply alerts, none of which have moved yet. /inventory-legacy
                  keeps the previous stock page reachable as a fallback while the
                  two are compared.
                */}
                <Route exact path='/inventory' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><Inventory /></ProtectedRoute>} replace={true} />
                <Route exact path='/inventory-legacy' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><LegacyInventory /></ProtectedRoute>} replace={true} />
                <Route exact path='/supply' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><Supply /></ProtectedRoute>} replace={true} />

                <Route exact path='/maintenance' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><Maintenance /></ProtectedRoute>} replace={true} />


                <Route exact path='/complaints' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><Complaints /></ProtectedRoute>} replace={true} />



                <Route exact path='/engineers' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><Engineers /></ProtectedRoute>} replace={true} />


                <Route exact path='/engineer' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><Engineer /></ProtectedRoute>} replace={true} />



                <Route exact path='/pantry' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><Pantry /></ProtectedRoute>} replace={true} />



                <Route exact path='/pantry-staff' element={<ProtectedRoute isLoggedIn={isLoggedIn} userInfo={userInfo}><PantryStaff /></ProtectedRoute>} replace={true} />
            </Routes>
        </Suspense>
    );

};

AppRoutes.propTypes = propTypes;

AppRoutes.defaultProps = defaultProps;

const mapStateToProps = (state) => ({
    isLoggedIn: state.loginReducer.isloggedIn,
    userInfo: state.loginReducer.userInfo,
});

const mapDispatchToProps = (dispatch) => ({
    enableLoader: () => dispatch({ type: 'TOGGLE_LOADER' })
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(AppRoutes);
